import classnames from "classnames";
import { useState } from "react";
import { useSelectedMultimediaSearchResult } from "./hooks/useSelectedMultimediaSearchResult";
import { MultimediaPreview } from "./MultimediaPreview";
import { MultimediaResultItemSkeleton } from "./MultimediaResultItemSkeleton";
import styles from "./MultimediaSearchResults.module.css";

import type { ResultActionConfig } from "@mirage/search/SearchResults/ResultRow";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import { SearchFilter } from "@mirage/shared/search/search-filters";
import type { SearchResult } from "@mirage/shared/search/search-result";
import type { SyntheticEvent } from "react";

type Size = { width: number; height: number };

export const MultimediaResultItem = ({
  result,
  className,
  query = "",
  onLoad,
  onError,
  onSizeSet,
  onClick,
  getActionConfig,
  annotationsEnabled,
  filters,
  resultPosition,
}: {
  result?: SearchResult;
  className?: string | undefined;
  query?: string;
  onLoad?: () => void;
  onError?: () => void;
  onSizeSet?: (size: Size) => void;
  onClick?: (res: SearchResult) => void;
  getActionConfig?: (result: SearchResult) => ResultActionConfig | undefined;
  annotationsEnabled?: boolean;
  filters?: SearchFilter[];
  resultPosition?: number;
}) => {
  const [size, setSize] = useState<Size | null>(null);
  const isMobileSize = useIsMobileSize();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { openMultimediaSearchResult } = useSelectedMultimediaSearchResult();
  const onImgLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const size = {
      width: e.currentTarget.naturalWidth,
      height: e.currentTarget.naturalHeight,
    };

    const normalizedSize = {
      width: Math.min(340, (220 * size.width) / size.height),
      height: 220,
    };
    setSize(normalizedSize);
    onSizeSet?.(normalizedSize);
    onLoad?.();
  };

  return (
    <div
      className={classnames(styles.item, className, {
        [styles.isMobile]: isMobileSize,
      })}
      style={{ flex: size ? `1 1 ${size.width}px` : undefined }}
    >
      {result ? (
        <MultimediaPreview
          result={result}
          query={query}
          onLoad={onImgLoad}
          onError={onError}
          className={styles.previewImg}
          onClick={onClick}
          actionConfig={getActionConfig?.(result)}
          annotationsEnabled={annotationsEnabled}
          filters={filters}
          resultPosition={resultPosition}
        />
      ) : (
        <MultimediaResultItemSkeleton />
      )}
    </div>
  );
};
